import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import { useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonPseudo,
  SidePanelLoader,
  PermissionEnum,
  Popup,
  PermissionDenied,
  ResponseError,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  FormValues,
  clearTransactionVolumeRiskFormState,
  getTransactionVolumeRiskValuesSuccess,
} from '../store/slices/volume-risk-form';
import Form from '../../../components/volumes-risk/transactions/item-form';
import {
  useLazyGetTransactionVolumesQuery,
  useCreateTransactionVolumeMutation,
  useUpdateTransactionVolumeRiskMutation,
} from '../store/services';
import {
  TransactionVolumeFormValues,
  TransactionVolumeTypeEnum,
  ClientType,
} from '../../types';

import ROUTES from '../../../routes';

export const schema = {
  type: 'object',
  properties: {
    riskScore: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Risk Score is required.',
      },
    },
    riskCategory: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Risk Category is required.',
      },
    },
    individualSingleTransactionFrom: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Field is required.',
      },
    },
    individualMultiTransactionFrom: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Field is required.',
      },
    },
    corporateSingleTransactionFrom: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Field is required.',
      },
    },
    corporateMultiTransactionFrom: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Field is required.',
      },
    },
  },
  required: [
    'riskScore',
    'riskCategory',
    'individualSingleTransactionFrom',
    'individualMultiTransactionFrom',
    'corporateSingleTransactionFrom',
    'corporateMultiTransactionFrom',
  ],
};

const TransactionVolumeRiskFormHOC = () => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue,
    setError,
  } = useForm<FormValues>({
    resolver: ajvResolver(schema as any),
    defaultValues: {
      riskScore: '',
      riskCategory: '',
      individualSingleTransactionFrom: '',
      individualSingleTransactionTo: '',
      individualSingleTransactionId: '',
      individualMultiTransactionFrom: '',
      individualMultiTransactionTo: '',
      individualMultiTransactionId: '',
      corporateSingleTransactionFrom: '',
      corporateSingleTransactionTo: '',
      corporateSingleTransactionId: '',
      corporateMultiTransactionFrom: '',
      corporateMultiTransactionTo: '',
      corporateMultiTransactionId: '',
      currencyId: '',
    },
  });

  const [defaultValues, setDefaultValues] = useState<{
    individualSingle: TransactionVolumeFormValues;
    indvidiualMulti: TransactionVolumeFormValues;
    corporateSingle: TransactionVolumeFormValues;
    corporateMulti: TransactionVolumeFormValues;
  }>();

  const currencyId = watch('currencyId');

  const {
    transactionsRisk: {
      volumesRisk: {
        form: { isLoading, formValues },
        list: { transactionVolumes, baseCurrency },
      },
    },
    amlDictionaries: { currencies, activePermissions, currenciesList },
  } = useAppSelector((state) => state);

  const [getTransationVolumesRisk] = useLazyGetTransactionVolumesQuery();
  const [
    createTransactionVolume,
    { isLoading: requestInProgress, isSuccess: submitSuccess },
  ] = useCreateTransactionVolumeMutation();
  const [
    updateTransactionVolume,
    { isLoading: updateInProgress, isSuccess: updateIsSuccess },
  ] = useUpdateTransactionVolumeRiskMutation();

  const { itemId } = useParams();
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const setVolumeError = ({
    nameFrom,
    nameTo,
    error,
  }: {
    nameFrom: keyof FormValues;
    nameTo: keyof FormValues;
    error: ResponseError;
  }) => {
    if (Array.isArray(error.data.message)) {
      error.data.message?.forEach((item) => {
        if (item.property === 'max') {
          setError(nameTo, {
            type: 'custom',
            message: item.constraints && Object.values(item.constraints)[0],
          });
        } else {
          setError(nameFrom, {
            type: 'custom',
            message: item.constraints && Object.values(item.constraints)[0],
          });
        }
      });
    }
  };

  const onFormSubmit = handleSubmit(async (values) => {
    const individualSingleBody: TransactionVolumeFormValues = {
      type: TransactionVolumeTypeEnum.SINGLE,
      customer: ClientType.INDIVIDUAL,
      min: Number(values.individualSingleTransactionFrom),
      riskScore: Number(values.riskScore),
      currency: values.currencyId || (baseCurrency?.id as string),
      riskCategory: values.riskCategory,
    };

    if (values.individualSingleTransactionTo) {
      individualSingleBody.max = Number(values.individualSingleTransactionTo);
    }

    const individualMultuBody: TransactionVolumeFormValues = {
      type: TransactionVolumeTypeEnum.MULTIPLE,
      customer: ClientType.INDIVIDUAL,
      min: Number(values.individualMultiTransactionFrom),
      riskScore: Number(values.riskScore),
      currency: values.currencyId || (baseCurrency?.id as string),
      riskCategory: values.riskCategory,
    };

    if (values.individualMultiTransactionTo) {
      individualMultuBody.max = Number(values.individualMultiTransactionTo);
    }

    const corporateSingleBody: TransactionVolumeFormValues = {
      type: TransactionVolumeTypeEnum.SINGLE,
      customer: ClientType.CORPORATE,
      min: Number(values.corporateSingleTransactionFrom),
      riskScore: Number(values.riskScore),
      currency: values.currencyId || (baseCurrency?.id as string),
      riskCategory: values.riskCategory,
    };

    if (values.corporateSingleTransactionTo) {
      corporateSingleBody.max = Number(values.corporateSingleTransactionTo);
    }

    const corporateMultuBody: TransactionVolumeFormValues = {
      type: TransactionVolumeTypeEnum.MULTIPLE,
      customer: ClientType.CORPORATE,
      min: Number(values.corporateMultiTransactionFrom),
      riskScore: Number(values.riskScore),
      currency: values.currencyId || (baseCurrency?.id as string),
      riskCategory: values.riskCategory,
    };

    if (values.corporateMultiTransactionTo) {
      corporateMultuBody.max = Number(values.corporateMultiTransactionTo);
    }

    if (!itemId) {
      await Promise.all(
        [
          individualSingleBody,
          individualMultuBody,
          corporateSingleBody,
          corporateMultuBody,
        ].map(async (item) => {
          await createTransactionVolume(item);
        }),
      );
    } else if (itemId) {
      if (!values.individualSingleTransactionId) {
        const individualSingleResponse = (await createTransactionVolume(
          individualSingleBody,
        )) as {
          error: ResponseError;
        };

        if (individualSingleResponse.error) {
          setVolumeError({
            error: individualSingleResponse.error,
            nameFrom: 'individualSingleTransactionFrom',
            nameTo: 'individualSingleTransactionTo',
          });
        }
      }
      if (!values.individualMultiTransactionId) {
        const individualMultiResponse = (await createTransactionVolume(
          individualMultuBody,
        )) as {
          error: ResponseError;
        };

        if (individualMultiResponse.error) {
          setVolumeError({
            error: individualMultiResponse.error,
            nameFrom: 'individualMultiTransactionFrom',
            nameTo: 'individualMultiTransactionTo',
          });
        }
      }
      if (!values.corporateSingleTransactionId) {
        const corporateSingleResponse = (await createTransactionVolume(
          corporateSingleBody,
        )) as {
          error: ResponseError;
        };

        if (corporateSingleResponse.error) {
          setVolumeError({
            error: corporateSingleResponse.error,
            nameFrom: 'corporateSingleTransactionFrom',
            nameTo: 'corporateSingleTransactionTo',
          });
        }
      }
      if (!values.corporateMultiTransactionId) {
        const corporateMultiResponse = (await createTransactionVolume(
          corporateMultuBody,
        )) as {
          error: ResponseError;
        };

        if (corporateMultiResponse.error) {
          setVolumeError({
            error: corporateMultiResponse.error,
            nameFrom: 'corporateMultiTransactionFrom',
            nameTo: 'corporateMultiTransactionTo',
          });
        }
      }

      if (!isEqual(individualSingleBody, defaultValues?.individualSingle)) {
        const individualSingleResponse = (await updateTransactionVolume({
          id: values?.individualSingleTransactionId as string,
          body: individualSingleBody,
        })) as {
          error: ResponseError;
        };

        if (individualSingleResponse.error) {
          setVolumeError({
            error: individualSingleResponse.error,
            nameFrom: 'individualSingleTransactionFrom',
            nameTo: 'individualSingleTransactionTo',
          });
        }
      }

      if (!isEqual(individualMultuBody, defaultValues?.indvidiualMulti)) {
        const individualMultiResponse = (await updateTransactionVolume({
          id: values?.individualMultiTransactionId as string,
          body: individualMultuBody,
        })) as {
          error: ResponseError;
        };

        if (individualMultiResponse.error) {
          setVolumeError({
            error: individualMultiResponse.error,
            nameFrom: 'individualMultiTransactionFrom',
            nameTo: 'individualMultiTransactionTo',
          });
        }
      }

      if (!isEqual(corporateSingleBody, defaultValues?.corporateSingle)) {
        const corporateSingleResponse = (await updateTransactionVolume({
          id: values?.corporateSingleTransactionId as string,
          body: corporateSingleBody,
        })) as {
          error: ResponseError;
        };

        if (corporateSingleResponse.error) {
          setVolumeError({
            error: corporateSingleResponse.error,
            nameFrom: 'corporateSingleTransactionFrom',
            nameTo: 'corporateSingleTransactionTo',
          });
        }
      }

      if (!isEqual(corporateMultuBody, defaultValues?.corporateMulti)) {
        const corporateMultiResponse = (await updateTransactionVolume({
          id: values?.corporateMultiTransactionId as string,
          body: corporateMultuBody,
        })) as {
          error: ResponseError;
        };

        if (corporateMultiResponse.error) {
          setVolumeError({
            error: corporateMultiResponse.error,
            nameFrom: 'corporateMultiTransactionFrom',
            nameTo: 'corporateMultiTransactionTo',
          });
        }
      }
    }
  });

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(`${ROUTES.TRANSACTIONS_RISK_VOLUME_RISK}${search || ''}`);
    }, 100);
  }, []);

  useEffect(() => {
    if (submitSuccess || updateIsSuccess) {
      handleClose();
    }
  }, [submitSuccess]);

  useEffect(() => {
    if (submitSuccess || updateIsSuccess) {
      getTransationVolumesRisk();
    }
  }, [submitSuccess, updateIsSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (itemId && transactionVolumes) {
      const currentVolumeRisk = transactionVolumes.find(
        (item) => item.id === itemId,
      );

      if (currentVolumeRisk) {
        dispatch(
          getTransactionVolumeRiskValuesSuccess({
            riskScore: currentVolumeRisk.score.toString(),
            riskCategory: currentVolumeRisk.riskCategory,
            individualSingleTransactionFrom:
              currentVolumeRisk.individualSingleTransaction?.from !== null
                ? (currentVolumeRisk.individualSingleTransaction?.from.toString() as string)
                : '',
            individualSingleTransactionTo:
              currentVolumeRisk.individualSingleTransaction?.to !== null
                ? (currentVolumeRisk.individualSingleTransaction?.to.toString() as string)
                : '',
            individualSingleTransactionId:
              currentVolumeRisk?.individualSingleTransaction?.id || '',
            individualMultiTransactionFrom:
              currentVolumeRisk.individualMultiTransaction?.from !== null
                ? (currentVolumeRisk.individualMultiTransaction?.from.toString() as string)
                : '',
            individualMultiTransactionTo:
              currentVolumeRisk.individualMultiTransaction?.to !== null
                ? (currentVolumeRisk.individualMultiTransaction?.to.toString() as string)
                : '',
            individualMultiTransactionId:
              currentVolumeRisk?.individualMultiTransaction?.id || '',
            corporateSingleTransactionFrom:
              currentVolumeRisk.corporateSingleTransacion?.from !== null
                ? (currentVolumeRisk.corporateSingleTransacion?.from.toString() as string)
                : '',
            corporateSingleTransactionTo:
              currentVolumeRisk.corporateSingleTransacion?.to !== null
                ? (currentVolumeRisk.corporateSingleTransacion?.to.toString() as string)
                : '',
            corporateSingleTransactionId:
              currentVolumeRisk.corporateSingleTransacion?.id || '',
            corporateMultiTransactionFrom:
              currentVolumeRisk.corporateMultiTransaction?.from !== null
                ? (currentVolumeRisk.corporateMultiTransaction?.from.toString() as string)
                : '',
            corporateMultiTransactionTo:
              currentVolumeRisk.corporateMultiTransaction?.to !== null
                ? (currentVolumeRisk.corporateMultiTransaction?.to.toString() as string)
                : '',
            corporateMultiTransactionId:
              currentVolumeRisk?.corporateMultiTransaction?.id || '',
            currencyId:
              currentVolumeRisk.individualSingleTransaction?.currency ||
              (baseCurrency?.id as string),
          }),
        );

        setDefaultValues({
          individualSingle: {
            type: TransactionVolumeTypeEnum.SINGLE,
            customer: ClientType.INDIVIDUAL,
            min:
              currentVolumeRisk.individualSingleTransaction?.from !== null
                ? Number(currentVolumeRisk.individualSingleTransaction?.from)
                : 0,
            max:
              currentVolumeRisk.individualSingleTransaction?.to !== null
                ? Number(currentVolumeRisk.individualSingleTransaction?.to)
                : undefined,
            riskScore: Number(currentVolumeRisk.score),
            currency:
              currentVolumeRisk.individualSingleTransaction?.currency ||
              (baseCurrency?.id as string),
            riskCategory: currentVolumeRisk.riskCategory,
          },
          indvidiualMulti: {
            type: TransactionVolumeTypeEnum.MULTIPLE,
            customer: ClientType.INDIVIDUAL,
            min:
              currentVolumeRisk.individualMultiTransaction?.from !== null
                ? Number(currentVolumeRisk.individualMultiTransaction?.from)
                : 0,
            max:
              currentVolumeRisk.individualMultiTransaction?.to !== null
                ? Number(currentVolumeRisk.individualMultiTransaction?.to)
                : undefined,
            riskScore: Number(currentVolumeRisk.score),
            currency:
              currentVolumeRisk.individualMultiTransaction?.currency ||
              (baseCurrency?.id as string),
            riskCategory: currentVolumeRisk.riskCategory,
          },
          corporateSingle: {
            type: TransactionVolumeTypeEnum.SINGLE,
            customer: ClientType.CORPORATE,
            min:
              currentVolumeRisk.corporateSingleTransacion?.from !== null
                ? Number(currentVolumeRisk.corporateSingleTransacion?.from)
                : 0,
            max:
              currentVolumeRisk.corporateSingleTransacion?.to !== null
                ? Number(currentVolumeRisk.corporateSingleTransacion?.to)
                : undefined,
            riskScore: Number(currentVolumeRisk.score),
            currency:
              currentVolumeRisk.corporateSingleTransacion?.currency ||
              (baseCurrency?.id as string),
            riskCategory: currentVolumeRisk.riskCategory,
          },
          corporateMulti: {
            type: TransactionVolumeTypeEnum.MULTIPLE,
            customer: ClientType.CORPORATE,
            min:
              currentVolumeRisk.corporateMultiTransaction?.from !== null
                ? Number(currentVolumeRisk.corporateMultiTransaction?.from)
                : 0,
            max:
              currentVolumeRisk.corporateMultiTransaction?.to !== null
                ? Number(currentVolumeRisk.corporateMultiTransaction?.to)
                : undefined,
            riskScore: Number(currentVolumeRisk.score),
            currency:
              currentVolumeRisk.corporateMultiTransaction?.currency ||
              (baseCurrency?.id as string),
            riskCategory: currentVolumeRisk.riskCategory,
          },
        });
      }
    }

    return () => {
      dispatch(clearTransactionVolumeRiskFormState());
    };
  }, [dispatch, itemId, transactionVolumes]);

  useEffect(() => {
    if (formValues && itemId) {
      reset(formValues);
    }
  }, [reset, formValues, itemId]);

  useEffect(() => {
    if (baseCurrency && !itemId) {
      reset({
        currencyId: baseCurrency.id,
      });
    }
  }, [baseCurrency]);

  const isEditable = activePermissions?.includes(
    PermissionEnum.SETTINGS_TRANSACTIONS_VOLUMES_EDIT_ACTION,
  );

  let title;
  let buttonText;
  let content = (
    <Form
      control={control}
      errors={errors}
      isEditable={isEditable}
      itemId={itemId}
      currencies={currencies}
      currencyId={currencyId}
      baseCurrency={baseCurrency}
      setValue={setValue}
      currenciesList={currenciesList}
    />
  );

  if (isLoading) {
    content = <SidePanelLoader />;
  } else if (
    !itemId &&
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_TRANSACTIONS_VOLUMES_CREATE_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  }

  if (itemId) {
    title = 'Edit risk group';
    buttonText = 'Update group';

    if (!isEditable) {
      title = 'Risk group info';
    }
  } else {
    title = 'Add risk group';
    buttonText = 'Save group';
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={title}
        sidepanelExpandedKey="risk-group"
        footer={
          <>
            {isEditable && (
              <ButtonPrimary
                text={
                  requestInProgress || updateInProgress
                    ? 'Loading...'
                    : buttonText
                }
                onClick={onFormSubmit}
                bold
              />
            )}

            <ButtonPseudo
              text="Cancel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default TransactionVolumeRiskFormHOC;
